import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Megaphone,
  Gift,
  Clock,
  Eye,
  Save,
  Palette,
  Calendar,
  Link as LinkIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =============================================
// ADMIN - Gestion du bandeau promotionnel
// =============================================

const PromoBannerAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({
    enabled: false,
    message: '',
    background_color: '#f97316',
    text_color: '#ffffff',
    icon: 'megaphone',
    promo_code: '',
    start_date: '',
    end_date: '',
    cta_text: '',
    cta_link: ''
  });

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const response = await axios.get(`${API}/settings/promo-banner`);
      if (response.data) {
        setBanner({
          enabled: response.data.enabled || false,
          message: response.data.title || '',
          background_color: response.data.backgroundColor || '#f97316',
          text_color: response.data.titleColor || '#ffffff',
          icon: 'megaphone',
          promo_code: response.data.subtitle?.replace('Code: ', '') || '',
          start_date: response.data.startDate ? response.data.startDate.split('T')[0] : '',
          end_date: response.data.endDate ? response.data.endDate.split('T')[0] : '',
          cta_text: response.data.linkText || '',
          cta_link: response.data.link || ''
        });
      }
    } catch (error) {
      console.log('No existing banner');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Use Body parameters (not query params) for the API
      await axios.put(`${API}/settings/promo-banner`, {
        enabled: banner.enabled,
        title: banner.message,
        backgroundColor: banner.background_color,
        titleColor: banner.text_color,
        linkText: banner.cta_text || null,
        link: banner.cta_link || null,
        startDate: banner.start_date || null,
        endDate: banner.end_date || null,
        subtitle: banner.promo_code ? `Code: ${banner.promo_code}` : ''
      });
      toast.success('Bandeau promotionnel mis à jour');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const iconOptions = [
    { value: 'megaphone', label: 'Mégaphone', icon: Megaphone },
    { value: 'gift', label: 'Cadeau', icon: Gift },
    { value: 'clock', label: 'Horloge', icon: Clock }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Bandeau Promotionnel</h1>
            <p className="text-slate-500">Affichez une promotion visible uniquement par vos clients</p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

        {/* Preview */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-sm">Aperçu</CardTitle>
              </div>
              <Badge variant={banner.enabled ? 'default' : 'secondary'}>
                {banner.enabled ? 'Actif' : 'Inactif'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div 
              className="rounded-lg py-3 px-4 flex items-center justify-center gap-3"
              style={{ backgroundColor: banner.background_color }}
            >
              {banner.icon === 'gift' ? (
                <Gift className="w-5 h-5" style={{ color: banner.text_color }} />
              ) : banner.icon === 'clock' ? (
                <Clock className="w-5 h-5" style={{ color: banner.text_color }} />
              ) : (
                <Megaphone className="w-5 h-5" style={{ color: banner.text_color }} />
              )}
              <p style={{ color: banner.text_color }} className="text-sm font-medium">
                {banner.message || 'Votre message promotionnel ici...'}
                {banner.promo_code && (
                  <span 
                    className="ml-2 px-2 py-0.5 rounded text-xs font-bold"
                    style={{ backgroundColor: `${banner.text_color}20` }}
                  >
                    Code: {banner.promo_code}
                  </span>
                )}
              </p>
              {banner.cta_text && (
                <span 
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${banner.text_color}30`, color: banner.text_color }}
                >
                  {banner.cta_text}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Activation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activation</CardTitle>
              <CardDescription>Activer ou désactiver le bandeau</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label>Afficher le bandeau</Label>
                <Switch
                  checked={banner.enabled}
                  onCheckedChange={(checked) => setBanner({ ...banner, enabled: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Message */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Message</CardTitle>
              <CardDescription>Le texte affiché dans le bandeau</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="Ex: -20% sur les heures de création jusqu'au 31 janvier !"
                value={banner.message}
                onChange={(e) => setBanner({ ...banner, message: e.target.value })}
              />
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-base">Couleurs</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-sm">Fond</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={banner.background_color}
                      onChange={(e) => setBanner({ ...banner, background_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={banner.background_color}
                      onChange={(e) => setBanner({ ...banner, background_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <Label className="text-sm">Texte</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={banner.text_color}
                      onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={banner.text_color}
                      onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Icon */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Icône</CardTitle>
              <CardDescription>Choisissez l'icône du bandeau</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                {iconOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setBanner({ ...banner, icon: opt.value })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        banner.icon === opt.value
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Promo Code */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Code Promo</CardTitle>
              <CardDescription>Optionnel - Affiché dans le bandeau</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="Ex: PROMO20"
                value={banner.promo_code}
                onChange={(e) => setBanner({ ...banner, promo_code: e.target.value.toUpperCase() })}
              />
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-base">Période</CardTitle>
              </div>
              <CardDescription>Optionnel - Dates de validité</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-sm">Date de début</Label>
                <Input
                  type="date"
                  value={banner.start_date}
                  onChange={(e) => setBanner({ ...banner, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Date de fin</Label>
                <Input
                  type="date"
                  value={banner.end_date}
                  onChange={(e) => setBanner({ ...banner, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-base">Bouton d'action (optionnel)</CardTitle>
              </div>
              <CardDescription>Ajoutez un lien vers une page spécifique</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-4">
              <div className="flex-1">
                <Label className="text-sm">Texte du bouton</Label>
                <Input
                  placeholder="Ex: En savoir plus"
                  value={banner.cta_text}
                  onChange={(e) => setBanner({ ...banner, cta_text: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm">Lien</Label>
                <Input
                  placeholder="https://..."
                  value={banner.cta_link}
                  onChange={(e) => setBanner({ ...banner, cta_link: e.target.value })}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PromoBannerAdminPage;
